import React, { useContext } from "react";
import { AxiosError } from "axios";
import { QueryResult, QueryKey, InfiniteQueryResult } from "react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "fe-shared/src/types/common";

import UserContext from "contexts/userContext";
import InvoiceStatus from "components/InvoiceStatus";
import InvoiceNavigation from "../../components/InvoiceNavigation";
import { ReturnIcon } from "fe-shared/src/components/Icons";
import {
  LinkButton,
  Loader,
  CollapseComponent,
  NotFoundPage,
  RecordsSidebar,
} from "fe-shared/src/components";
import {
  DialogHeader,
  InvoiceHeaderInfo,
  InvoiceContentView,
} from "fe-shared-transactions/src/components";

import { PermissionLevel, UserPermission } from "fe-shared/src/types/domain";
import {
  hasAnyPermission,
  hasPermission,
} from "fe-shared/src/helpers/permissions";
import { Invoice } from "fe-shared-transactions/src/types";

import { PERMISSIONS, INVOICES_ACTIVITIES_QUERY_KEY } from "consts";

import InvoiceActionsContainer from "./containers/InvoiceActionsContainer";
import { NavigationInvoices } from "./modules/api";

import * as api from "./modules/api";

import styles from "./InvoicesDetailsView.module.scss";

interface Props {
  returnUrl: string;
  invoice: QueryResult<Invoice, AxiosError<ApiError>>;
  invoiceQueryKey: QueryKey;
  isFetching: boolean;
  isWithNavigation: boolean;
  isDirectNavigation: boolean;
  invoiceListInfo: QueryResult<api.InvoiceListInfo, AxiosError<ApiError>>; 
  navigationInvoices: InfiniteQueryResult<
    NavigationInvoices,
    AxiosError<ApiError>
  >;
}

const InvoicesDetailsView = (props: Props) => {
  const {
    returnUrl ,
    invoice,
    invoiceQueryKey,
    isFetching,
    isWithNavigation,
    navigationInvoices,
  } = props;
  const { data, isLoading, isError } = invoice;

  const { t } = useTranslation();
  const currentUser = useContext(UserContext);
  const hasSidebar = hasAnyPermission(currentUser?.permissions, [
    {
      name: PERMISSIONS.invoiceHistory,
      level: PermissionLevel.Read,
    },
    {
      name: PERMISSIONS.invoiceNotes,
      level: PermissionLevel.Read,
    },
  ]);
  const canAssign = hasPermission(currentUser?.permissions, {
    name: PERMISSIONS.invoiceDetailsAssign,
    level: PermissionLevel.Full,
  });

  const isPermitted = (permission: UserPermission | UserPermission[]) => {
    if (Array.isArray(permission)) {
      return permission.some((item) =>
        hasPermission(currentUser?.permissions, item)
      );
    }
    return hasPermission(currentUser?.permissions, permission);
  };

  const canViewHistory = isPermitted({
    name: PERMISSIONS.invoiceHistory,
    level: PermissionLevel.Read,
  });

  const canExportHistory = isPermitted({
    name: PERMISSIONS.invoiceHistoryExport,
    level: PermissionLevel.Full,
  });

  const canViewNotes = isPermitted({
    name: PERMISSIONS.invoiceNotes,
    level: PermissionLevel.Read,
  });
  const canAddNote = isPermitted({
    name: PERMISSIONS.invoiceAddNote,
    level: PermissionLevel.Full,
  });

  const canExportNotes = isPermitted({
    name: PERMISSIONS.invoiceNotesExport,
    level: PermissionLevel.Full,
  });

  const isInvoiceNotFound = invoice.error?.response?.status === 404;

  const isCurrentUserAssignee = data?.assignee?.userId === currentUser?.userId;

  const renderContent = () => {
    const { invoiceNumber, invoiceId } = data!;

    const activitiesQueryKey: QueryKey = [
      INVOICES_ACTIVITIES_QUERY_KEY,
      invoiceNumber,
    ];

    const getDialogTitle = (title: string) => (
      <DialogHeader
        title={title}
        type="Invoice"
        number={invoiceNumber}
        translationRoot="invoices"
      />
    );

    return (
      <>
        <main className={styles.reqDetailsContent}>
          <div className={styles.reqDetailsContentWrapper}>
            <div className={styles.reqDetailsNavigation}>
              <LinkButton
                to={returnUrl}
                variant="inline"
                id="request-back-link"
                iconComponent={ReturnIcon}
                className={styles.reqDetailsBackLink}
              >
                {t("invoices.grid.title")}
              </LinkButton>
              {isWithNavigation && (
                  <InvoiceNavigation
                    navigationInvoices={navigationInvoices}
                    invoiceListInfo={props.invoiceListInfo}
                    isDirectnavigation={props.isDirectNavigation}
                  />
              )}
            </div>
            <div className={styles.reqDetailsData}>
              <InvoiceStatus
                invoice={data!}
                canAssign={canAssign}
                activitiesQueryKey={activitiesQueryKey}
                invoiceQueryKey={invoiceQueryKey}
              />
              <InvoiceHeaderInfo isStaffView invoice={data!} />
              <InvoiceContentView invoice={data!} />

              {isCurrentUserAssignee && (
                <InvoiceActionsContainer
                  invoice={data!}
                  activitiesQueryKey={activitiesQueryKey}
                  invoiceDetailsQueryKey={invoiceQueryKey}
                />
              )}
            </div>
          </div>
        </main>
        {hasSidebar && (
          <CollapseComponent storageKey="sidebarCollapsed">
            <RecordsSidebar
              objectId={invoiceNumber}
              entityType="invoices"
              entityName={t("invoices.details.entityName")}
              initiatorEmail={currentUser?.email || ""}
              getDialogTitle={getDialogTitle}
              fileNamePrefix={t("invoices.details.fileNamePrefix", {
                invoiceNumber,
              })}
              fileNameSuffix=""
              canViewHistory={canViewHistory}
              canExportHistory={canExportHistory}
              activitiesQueryKey={activitiesQueryKey}
              canViewNotes={canViewNotes}
              canAddNote={canAddNote}
              canExportNotes={canExportNotes}
              notesQueryKey={["invoiveNotes", invoiceNumber]}
              getNotes={() => api.getInvoiceNotes(invoiceId)}
              addNote={(text) => {
                return api.addInvoiceNote({ invoiceId, text });
              }}
            />
          </CollapseComponent>
        )}
      </>
    );
  };
  return (
    <div className={styles.reqDetails}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && data && renderContent()}
      {isError && (
        <NotFoundPage
          returnLink="/invoices"
          title={
            isInvoiceNotFound
              ? "invoices.details.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["invoices.details.error.description"]}
          returnLinkDescription="invoices.details.error.returnLink"
        />
      )}
    </div>
  );
};

export default InvoicesDetailsView;
